import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock,
  MessageSquare,
  Clock,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Phone,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { dashboardApi } from "@/lib/crmApi";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.round(diff / 3600000);
    if (h < 1) return "à l'instant";
    if (h < 24) return `il y a ${h}h`;
    const d = Math.round(h / 24);
    return `il y a ${d}j`;
  } catch {
    return "";
  }
}

function LeadLink({ lead, children }) {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav(`/leads/${lead.id}`)}
      className="group flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition hover:bg-muted/60"
      data-testid={`todo-lead-${lead.id}`}
    >
      <div className="min-w-0 flex-1">{children}</div>
      <ChevronRight className="h-3.5 w-3.5 flex-none text-muted-foreground transition group-hover:translate-x-0.5" />
    </button>
  );
}

function Section({ icon: Icon, title, count, color, children, empty }) {
  return (
    <section
      className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft"
      data-testid={`todo-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <header className="mb-3 flex items-center gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="font-heading text-sm font-bold flex-1">{title}</h3>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums">
          {count}
        </span>
      </header>
      <Separator className="mb-2" />
      {count === 0 ? (
        <p className="py-3 text-center text-xs text-muted-foreground">
          {empty || "Rien à traiter"}
        </p>
      ) : (
        <div className="space-y-0.5">{children}</div>
      )}
    </section>
  );
}

export default function TodoTodayPanel({ projetId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await dashboardApi.today({ projetId }));
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projetId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const t = data?.totals || {
    rdv_today: 0, unread_replies: 0, stale_proposals: 0, no_reply_4h: 0, total: 0,
  };
  const b = data?.buckets || {};

  return (
    <section
      className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6"
      data-testid="todo-today-panel"
    >
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold">À traiter aujourd'hui</h2>
            <p className="text-xs text-muted-foreground">
              {t.total} action{t.total > 1 ? "s" : ""} en attente · refresh auto chaque minute
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="gap-1.5"
          data-testid="todo-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </header>

      {loading && !data ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Chargement…
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Section
            icon={CalendarClock}
            title="RDV dans les 24h"
            count={t.rdv_today}
            color="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
            empty="Aucun RDV programmé"
          >
            {(b.rdv_today || []).map((l) => (
              <LeadLink key={l.id} lead={l}>
                <div className="font-medium truncate">{l.nom}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                  <CalendarClock className="h-3 w-3" />
                  {fmtDate(l.rdv_at)}
                  {l.rdv_kind && <span className="rounded bg-muted px-1.5">{l.rdv_kind}</span>}
                </div>
              </LeadLink>
            ))}
          </Section>

          <Section
            icon={MessageSquare}
            title="Réponses non lues"
            count={t.unread_replies}
            color="bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
            empty="Tout est lu"
          >
            {(b.unread_replies || []).map((l) => (
              <LeadLink key={l.id} lead={l}>
                <div className="font-medium truncate">{l.nom}</div>
                <div className="text-[11px] text-muted-foreground italic line-clamp-1">
                  « {l.last_inbound_preview} »
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {relTime(l.last_inbound_at)}
                </div>
              </LeadLink>
            ))}
          </Section>

          <Section
            icon={Clock}
            title="Propositions sans réponse > 48h"
            count={t.stale_proposals}
            color="bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300"
            empty="Aucune proposition stagnante"
          >
            {(b.stale_proposals || []).map((l) => (
              <LeadLink key={l.id} lead={l}>
                <div className="font-medium truncate">{l.nom}</div>
                <div className="text-[11px] text-muted-foreground">
                  Dernier envoi {relTime(l.last_outbound_at)}
                </div>
              </LeadLink>
            ))}
          </Section>

          <Section
            icon={Phone}
            title="Pas de réponse (4h+)"
            count={t.no_reply_4h}
            color="bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
            empty="Tous les leads ont répondu"
          >
            {(b.no_reply_4h || []).map((l) => (
              <LeadLink key={l.id} lead={l}>
                <div className="font-medium truncate">{l.nom}</div>
                <div className="text-[11px] text-muted-foreground">{l.telephone}</div>
              </LeadLink>
            ))}
          </Section>
        </div>
      )}
    </section>
  );
}
